import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AirbnbHomeMapSection } from "@/components/home/AirbnbHomeMapSection";
import { Button } from "@/components/ui/button";
import { dictionary } from "@/i18n/translations";
import { api } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

const HERO_VIDEO_URL = "/hero-luxury.mp4";

export default function HomePage({ lang }) {
  const copy = dictionary[lang];
  const [selectedSubArea, setSelectedSubArea] = useState("all");
  const [subAreas, setSubAreas] = useState([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: copy.seo.homeTitle,
      description: copy.seo.homeDescription,
    });
  }, [copy.seo.homeDescription, copy.seo.homeTitle, lang]);

  useEffect(() => {
    const fetchMakkahSubAreas = async () => {
      setMapLoading(true);
      try {
        const response = await api.get("/regions/makkah/sub-areas");
        setSubAreas(Array.isArray(response.data) ? response.data : []);
      } catch {
        toast.error(copy.common.requestFailed);
      } finally {
        setMapLoading(false);
      }
    };

    fetchMakkahSubAreas();
  }, [copy.common.requestFailed]);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const subAreaParam =
          selectedSubArea === "all" ? "" : `&sub_area=${selectedSubArea}`;
        const response = await api.get(`/properties?lang=${lang}&region=makkah${subAreaParam}`);
        setProperties(Array.isArray(response.data) ? response.data : []);
      } catch {
        toast.error(copy.common.requestFailed);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [copy.common.requestFailed, lang, selectedSubArea]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-luxury-surface" data-testid="homepage-root">
      <header className="relative min-h-screen overflow-hidden" data-testid="homepage-hero-section">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
          src={HERO_VIDEO_URL}
          data-testid="homepage-hero-video"
        />
        <div className="hero-overlay absolute inset-0" data-testid="homepage-hero-overlay" />

        <div className="relative mx-auto flex min-h-screen max-w-7xl items-center px-4 pb-20 pt-40 sm:px-10">
          <div className="max-w-3xl space-y-7 animate-rise-up" data-testid="homepage-hero-content">
            <h1 className="text-4xl font-semibold leading-tight text-cream-50 sm:text-5xl lg:text-6xl" data-testid="homepage-hero-title">
              {copy.hero.title}
            </h1>
            <p className="max-w-2xl text-base text-cream-100 sm:text-lg" data-testid="homepage-hero-subtitle">
              {copy.hero.subtitle}
            </p>

            <div className="flex flex-wrap gap-3" data-testid="homepage-hero-actions">
              <a href="#properties" data-testid="homepage-hero-primary-link">
                <Button className="rounded-full bg-gold-600 px-7 py-6 text-sm text-stone-900 hover:bg-gold-500" data-testid="homepage-hero-primary-button">
                  {copy.hero.primaryCta}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl min-w-0 space-y-14 px-4 pb-20 pt-14 sm:px-10" data-testid="homepage-main-content">
        <section id="map" className="space-y-3" data-testid="homepage-region-header-section">
          <h2 className="text-base text-stone-500" data-testid="homepage-region-subtitle">{copy.mapExperience.sectionSubtitle}</h2>
          <h3 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="homepage-region-title">
            {copy.mapExperience.sectionTitle}
          </h3>
        </section>

        <AirbnbHomeMapSection
          lang={lang}
          copy={copy}
          loading={loading || mapLoading}
          properties={properties}
          subAreas={subAreas}
          selectedSubArea={selectedSubArea}
          onSubAreaChange={setSelectedSubArea}
        />

      </main>
    </div>
  );
}
